import { useSyncExternalStore } from 'react';

/** @see https://developer.mozilla.org/en-US/docs/Web/API/BeforeInstallPromptEvent */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

let deferred: BeforeInstallPromptEvent | null = null;
const listeners = new Set<() => void>();

function setDeferred(event: BeforeInstallPromptEvent | null) {
  deferred = event;
  listeners.forEach((listener) => listener());
}

window.addEventListener('beforeinstallprompt', (e) => setDeferred(e as BeforeInstallPromptEvent));
window.addEventListener('appinstalled', () => setDeferred(null));

const isStandalone =
  matchMedia('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true;

const isMobile = matchMedia('(pointer: coarse)').matches;

const isIos =
  /iPhone|iPad|iPod/.test(navigator.userAgent) ||
  (navigator.userAgent.includes('Macintosh') && navigator.maxTouchPoints > 1);

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** 'prompt' when the browser offers a native install dialog, 'ios' when it has to be added via the share menu */
export function useInstallPrompt() {
  const event = useSyncExternalStore(subscribe, () => deferred);
  const mode: 'prompt' | 'ios' | null = isStandalone || !isMobile ? null : event ? 'prompt' : isIos ? 'ios' : null;

  async function install() {
    if (!deferred) return;
    await deferred.prompt();
    await deferred.userChoice;
    setDeferred(null);
  }

  return { mode, install };
}
